import { ANALYTICS_CHART_START_TIMESTAMP } from 'constants/chart'
import { useMemo } from 'react'
import { UnifiedPoolData, UnifiedTokenData } from 'types/analytics'
import { AnalyticsChartSeries } from 'utils/analytics/analyticsChartSeries'

import { useV2ChartData } from './useV2ChartData'
import { useV2PoolDatas } from './useV2PoolDatas'
import { useV2TokenDatas } from './useV2TokenDatas'
import { useV3ChartData } from './useV3ChartData'
import { useV3PoolDatas } from './useV3PoolDatas'
import { useTokenDatasWithChanges } from './useV3TokenDatas'

type VersionView = 'v2' | 'v3'
type TableKind = 'pools' | 'tokens'

type ChartDatum = {
  date: number
  volumeUSD: number
  tvlUSD: number
  feesUSD: number
}

interface Dataset<T> {
  data?: T
  isLoading: boolean
  isFetching?: boolean
  refetch?: () => Promise<unknown>
}

interface AnalyticsDataParams {
  versionView: VersionView
  tableKind: TableKind
  timestamps?: [number, number, number]
  blocks?: Map<number, number>
  latestIndexedBlockNumber?: number
}

interface ChartDataset<T> extends Dataset<T> {
  series?: AnalyticsChartSeries
  latestEntry?: ChartDatum
}

interface AnalyticsDataResult {
  charts: ChartDataset<ChartDatum[] | undefined>
  pools: {
    v2: Dataset<UnifiedPoolData[] | undefined>
    v3: Dataset<UnifiedPoolData[] | undefined>
  }
  tokens: {
    v2: Dataset<UnifiedTokenData[] | undefined>
    v3: Dataset<UnifiedTokenData[] | undefined>
  }
}

export function useAnalyticsData({
  versionView,
  tableKind,
  timestamps,
  blocks,
  latestIndexedBlockNumber,
}: AnalyticsDataParams): AnalyticsDataResult {
  const hasHistoryWindow = Boolean(timestamps)
  const isV3 = useMemo(() => versionView === 'v3', [versionView])
  const isV2 = useMemo(() => versionView === 'v2', [versionView])

  const v3ChartQuery = useV3ChartData(ANALYTICS_CHART_START_TIMESTAMP, 0, {
    enabled: isV3,
  })
  const v2ChartQuery = useV2ChartData({
    enabled: isV2,
    startTime: ANALYTICS_CHART_START_TIMESTAMP,
  })

  const chartData = useMemo(
    () => (isV3 ? v3ChartQuery.data : v2ChartQuery.data),
    [isV3, v2ChartQuery.data, v3ChartQuery.data]
  )

  const chartSeries = useMemo<AnalyticsChartSeries | undefined>(() => {
    const prepared = isV3 ? v3ChartQuery.prepared : v2ChartQuery.prepared
    if (!prepared || prepared.raw.length === 0) return undefined
    return prepared
  }, [isV3, v3ChartQuery.prepared, v2ChartQuery.prepared])

  const latestChartEntry = useMemo<ChartDatum | undefined>(
    () => chartSeries?.latestEntry as ChartDatum | undefined,
    [chartSeries]
  )

  const chartsLoading = useMemo(
    () => (isV3 ? v3ChartQuery.isLoading : v2ChartQuery.isLoading),
    [isV3, v2ChartQuery.isLoading, v3ChartQuery.isLoading]
  )

  const v3PoolsQuery = useV3PoolDatas(undefined, {
    enabled: isV3 && tableKind === 'pools' && hasHistoryWindow,
    timestamps,
    blocks,
    latestIndexedBlockNumber,
    blocksRequired: Boolean(blocks),
    include48hSnapshot: false,
  })

  const v3TokensQuery = useTokenDatasWithChanges({
    enabled: isV3 && tableKind === 'tokens' && hasHistoryWindow,
    timestamps,
    blocks,
    latestIndexedBlockNumber,
    blocksRequired: Boolean(blocks),
  })

  const v2PoolsQuery = useV2PoolDatas(undefined, {
    enabled: isV2 && tableKind === 'pools' && hasHistoryWindow,
    timestamps,
    blocks,
    latestIndexedBlockNumber,
    blocksRequired: Boolean(blocks),
    include48hSnapshot: false,
  })

  const v2TokensQuery = useV2TokenDatas(undefined, {
    enabled: isV2 && tableKind === 'tokens' && hasHistoryWindow,
    timestamps,
    blocks,
    latestIndexedBlockNumber,
    blocksRequired: Boolean(blocks),
  })

  return {
    charts: {
      data: chartData,
      series: chartSeries,
      latestEntry: latestChartEntry,
      isLoading: chartsLoading,
      isFetching: isV3 ? v3ChartQuery.isFetching : v2ChartQuery.isFetching,
      refetch: isV3 ? () => v3ChartQuery.refetch() : () => v2ChartQuery.refetch(),
    },
    pools: {
      v2: {
        data: v2PoolsQuery.data,
        isLoading: v2PoolsQuery.isLoading,
        isFetching: v2PoolsQuery.isFetching,
        refetch: () => v2PoolsQuery.refetch(),
      },
      v3: {
        data: v3PoolsQuery.data,
        isLoading: v3PoolsQuery.isLoading,
        isFetching: v3PoolsQuery.isFetching,
        refetch: () => v3PoolsQuery.refetch(),
      },
    },
    tokens: {
      v2: {
        data: v2TokensQuery.data,
        isLoading: v2TokensQuery.isLoading,
        isFetching: v2TokensQuery.isFetching,
        refetch: () => v2TokensQuery.refetch(),
      },
      v3: {
        data: v3TokensQuery.data,
        isLoading: v3TokensQuery.isLoading,
        isFetching: v3TokensQuery.isFetching,
        refetch: () => v3TokensQuery.refetch(),
      },
    },
  }
}
